import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Dropdown, Form, Grid, Icon, Radio } from 'components/HeroSemantic';
import { useDispatch, useSelector } from 'react-redux';
import { selected2FADeliveryMethod } from 'state/modules/auth';
import { auth2FaRecipientTypes, auth2FaDeliveryMethods } from '../../config';

const recipientTypeDeliveryMethods = {
  [auth2FaRecipientTypes.MOBILE]: [auth2FaDeliveryMethods.SMS, auth2FaDeliveryMethods.VOICE],
  [auth2FaRecipientTypes.FIXED_LINE]: [auth2FaDeliveryMethods.VOICE],
  [auth2FaRecipientTypes.FIXED_LINE_OR_MOBILE]: [auth2FaDeliveryMethods.VOICE, auth2FaDeliveryMethods.SMS],
};

const RecipientTypeIcon = ({ type }) => {
  switch (type) {
    case auth2FaRecipientTypes.MOBILE:
      return <Icon name="mobile" />;
    case auth2FaRecipientTypes.FIXED_LINE:
    case auth2FaRecipientTypes.FIXED_LINE_OR_MOBILE:
      return <Icon name="phone" />;
    default:
      return <Icon name="question" />;
  }
};

RecipientTypeIcon.propTypes = {
  type: PropTypes.string.isRequired,
};

const TwoFactorTypeForm = ({ isAdmin, onSubmit, recipients }) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const { twoFactorDeliveryMethod } = useSelector(state => state.auth);
  const subtitleId = isAdmin ? 'page.user.twoFactorAuth.subtitle.admin' : 'page.user.twoFactorAuth.selectAuthType';

  const getDeliveryMethodsForRecipient = recipient => {
    const deliveryOptions = recipientTypeDeliveryMethods[recipient.type];

    // Return the recipient type's delivery methods, or default to voice if not found.
    return deliveryOptions || [auth2FaDeliveryMethods.VOICE, auth2FaDeliveryMethods.SMS];
  };

  const [selectedRecipient, setSelectedRecipient] = useState(recipients[0]);
  const canSubmit = selectedRecipient && twoFactorDeliveryMethod;

  const handleRecipientOptionChange = recipient => {
    setSelectedRecipient(recipient);

    // If the delivery method is not available for this newly selected recipient type, asjust the current selected delivery method.
    if (twoFactorDeliveryMethod) {
      const recipientDeliveryMethods = getDeliveryMethodsForRecipient(recipient);

      if (recipientDeliveryMethods.includes(twoFactorDeliveryMethod)) {
        // The newly selected delivery method is still available for the new recipient type, no need to adjust.
        return;
      }

      // The currently selected delivery method is not available for the new recipient type, so reset it.
      // If the available delivery methods for the new recipient type only has 1 option, just select it, otherwise set as null.
      dispatch(selected2FADeliveryMethod(recipientDeliveryMethods.length === 1 ? recipientDeliveryMethods[0] : null));
    }
  };

  const handleDeliveryMethodOptionChange = value => {
    dispatch(selected2FADeliveryMethod(value));
  };

  const handleFormSubmit = () => {
    if (canSubmit) {
      onSubmit(selectedRecipient.hash, twoFactorDeliveryMethod);
    }
  };

  if (!Array.isArray(recipients) || recipients?.length === 0) {
    return null;
  }

  return (
    <Form onSubmit={handleFormSubmit}>
      <Grid style={{ display: 'flex', flexDirection: 'column' }}>
        <Grid.Row><FormattedMessage id={subtitleId} /></Grid.Row>
        <Grid.Row style={{ padding: 0 }}>
          {formatMessage({ id: 'page.user.twoFactorAuth.recipient' })}
          :
        </Grid.Row>
        <Grid.Row style={{ padding: '4px 0' }}>
          {/* Flex column so recipient rows sit tight against each other
              instead of inheriting Semantic UI's default row/label margins. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
            {recipients.map(recipient => {
              const selectedBackground = recipient.hash === selectedRecipient.hash ?
                'color-mix(in srgb, var(--tenant-primary) 12%, var(--owner-surface))'
                : 'transparent';
              return (
                <Radio
                  key={recipient.hash}
                  id={recipient.hash}
                  name={recipient.hash}
                  style={{
                    margin: 0,
                    cursor: 'pointer',
                    backgroundColor: selectedBackground,
                    padding: '6px 8px',
                    width: '100%',
                  }}
                  label={(
                    <>
                      <RecipientTypeIcon type={recipient.type} />
                      <FormattedMessage id="page.user.twoFactorAuth.verifiedNumber" values={{ value: recipient.value }} />
                    </>
                  )}
                  value={recipient.hash}
                  onChange={() => handleRecipientOptionChange(recipient)}
                  checked={recipient.hash === selectedRecipient.hash}
                />
              );
            })}
          </div>
        </Grid.Row>
        <Grid.Row style={{ padding: 0 }}>
          {formatMessage({ id: 'page.user.twoFactorAuth.deliveryMethod.title' })}
          :
        </Grid.Row>
        <Grid.Row centered>
          <Dropdown
            style={{ width: '100%' }}
            id="deliveryMethod"
            placeholder={formatMessage({ id: 'page.user.twoFactorAuth.deliveryMethod.select' })}
            defaultChecked={twoFactorDeliveryMethod}
            value={twoFactorDeliveryMethod}
            onChange={(e, { value }) => handleDeliveryMethodOptionChange(value)}
            options={
              getDeliveryMethodsForRecipient(selectedRecipient).map(option => (
                { key: option, text: formatMessage({ id: `page.user.twoFactorAuth.deliveryMethod.${option}` }), value: option }
              ))
            }
            selection
            required
          />
        </Grid.Row>
        <Grid.Row centered>
          <Button type="submit" color="green" size="large" disabled={!canSubmit}>
            <FormattedMessage id="general.send" />
          </Button>
        </Grid.Row>
      </Grid>
    </Form>
  );
};

TwoFactorTypeForm.propTypes = {
  isAdmin: PropTypes.bool,
  recipients: PropTypes.arrayOf(PropTypes.object).isRequired,
  onSubmit: PropTypes.func.isRequired,
};

TwoFactorTypeForm.defaultProps = {
  isAdmin: false,
};

export default TwoFactorTypeForm;
